import React from "react";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "./css/Privacy.css";

const Privacy = () => {
  return (
    <>
    <SEO
        title="Privacy Policy - Joujo Safaris"
        description="These Terms of Service constitute a legally binding agreement between Joujo Safaris (“we,” “us,” or “our”) 
              and you (“the client,” “you,” or “your”). By accessing our website, booking a safari, or using any of our services, 
              you accept and agree to be bound by these Terms."
        url={`https://joujosafaris.com/terms`}
        image=""
        schema={{
          "@context": "https://schema.org",
          "@type": "PrivacyPolicy",
          name: "Privacy Policy - Joujo Safaris",
          description: `These Privacy Policy constitute a legally binding agreement between Joujo Safaris (“we,” “us,” or “our”) 
              and you (“the client,” “you,” or “your”). By accessing our website, booking a safari, or using any of our services, 
              you accept and agree to be bound by this Policy.`,
          image: "",
          provider: {
            "@type": "TravelAgency",
            name: "Joujo Safaris",
            url: "https://joujosafaris.com",
          },
        }}
      />
      <Navbar />
      <section className="privacy-hero">
        <div className="privacy-hero__inner">
          <h1 className="privacy-title"><i className="fas fa-user-shield"></i> Privacy Policy</h1>
          <p className="privacy-subtitle">Effective Date: <strong>1ST SEPTEMBER 2025</strong></p>
        </div>
      </section>

      <main className="privacy-wrapper">
        <aside className="privacy-toc">
          <h3><i className="fas fa-list-ul"></i> Contents</h3>
          <nav>
            <a href="#info-we-collect">1. Information We Collect</a>
            <a href="#how-we-use">2. How We Use Your Information</a>
            <a href="#sharing">3. Sharing Your Information</a>
            <a href="#retention">4. Data Retention</a>
            <a href="#security">5. Security</a>
            <a href="#your-rights">6. Your Rights</a>
            <a href="#cookies">7. Cookies & Tracking</a>
            <a href="#third-parties">8. Third-Party Links</a>
            <a href="#transfers">9. International Transfers</a>
            <a href="#children">10. Children’s Privacy</a>
            <a href="#changes">11. Changes to This Policy</a>
            <a href="#contact">12. Contact Us</a>
          </nav>
        </aside>

        <section className="privacy-content">
          <div className="notice-card info">
            <i className="fas fa-info-circle"></i>
            <p>
              By using our website or booking our safaris, you agree to this Privacy Policy.
              If you do not agree, please do not use our services.
            </p>
          </div>

          <article id="info-we-collect" className="privacy-block">
            <h2><span>1.</span> Information We Collect</h2>
            <div className="grid-2">
              <div className="card">
                <h3><i className="fas fa-user"></i> Information You Provide</h3>
                <ul className="bulleted">
                  <li>Full name, email, phone number</li>
                  <li>Billing address and payment details (processed via secure gateways)</li>
                  <li>Passport/ID info (where required for travel bookings)</li>
                  <li>Emergency contact information</li>
                </ul>
              </div>
              <div className="card">
                <h3><i className="fas fa-laptop-code"></i> Information Collected Automatically</h3>
                <ul className="bulleted">
                  <li>IP address, device, browser, and OS</li>
                  <li>Pages viewed, timestamps, and referring URLs</li>
                  <li>Approximate location (derived from IP or browser used)</li>
                </ul>
              </div>
            </div>
            <div className="card">
              <h3><i className="fas fa-plug"></i> Information from Third Parties</h3>
              <ul className="bulleted">
                <li>Social login providers (e.g., Google/Facebook) - <strong>NOT</strong> yet used</li>
                <li>Partner lodges, airlines, and agencies for booking fulfillment</li>
              </ul>
            </div>
          </article>

          <article id="how-we-use" className="privacy-block">
            <h2><span>2.</span> How We Use Your Information</h2>
            <ul className="checklist">
              <li>Process bookings, payments, confirmations, and itineraries</li>
              <li>Provide customer support and trip updates</li>
              <li>Improve our website, products, and services</li>
              <li>Send marketing (only if you opt in; you can opt out anytime)</li>
              <li>Comply with legal obligations (e.g., immigration requirements)</li>
              <li>Prevent fraud and maintain security</li>
            </ul>
          </article>

          <article id="sharing" className="privacy-block">
            <h2><span>3.</span> Sharing Your Information</h2>
            <p>We do not sell your personal information. We may share it with:</p>
            <ul className="bulleted">
              <li><strong>Service providers</strong> (payments, hosting, IT, email, analytics)</li>
              <li><strong>Travel partners</strong> (lodges, airlines, guides, insurers) to fulfill bookings</li>
              <li><strong>Authorities</strong> when required by law or to protect our rights/users</li>
              <li><strong>Business transfers</strong> (merger, acquisition, or asset sale)</li>
            </ul>
            <div className="notice-card">
              <i className="fas fa-lock"></i>
              <p>Payment details are handled by secure payment processors; we do not store full card numbers.</p>
            </div>
          </article>

          <article id="retention" className="privacy-block">
            <h2><span>4.</span> Data Retention</h2>
            <p>
              We keep your data only as long as necessary to provide services, meet legal/regulatory obligations,
              resolve disputes, and enforce our agreements. You may request deletion at any time (see “Your Rights”).
            </p>
          </article>

          <article id="security" className="privacy-block">
            <h2><span>5.</span> Security</h2>
            <ul className="bulleted">
              <li>HTTPS (SSL/TLS) encryption</li>
              <li>Access controls and role-based permissions</li>
              <li>Regular updates and security reviews</li>
            </ul>
            <p><em>No system is 100% secure; we cannot guarantee absolute security.</em></p>
          </article>

          <article id="your-rights" className="privacy-block">
            <h2><span>6.</span> Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="checklist">
              <li>Access and obtain a copy of your personal data</li>
              <li>Correct inaccuracies or update your information</li>
              <li>Request deletion (“right to be forgotten”)</li>
              <li>Restrict or object to certain processing</li>
              <li>Port your data to another provider (where applicable)</li>
              <li>Withdraw marketing consent at any time</li>
            </ul>
            <p>To exercise any right, contact us at <a href="mailto:support@joujosafaris.com">support@joujosafaris.com</a>. We may verify your identity before responding.</p>
          </article>

          <article id="cookies" className="privacy-block">
            <h2><span>7.</span> Cookies & Tracking</h2>
            <p>We use cookies and similar technologies to enhance functionality, remember preferences, and analyze usage.</p>
            <ul className="bulleted">
              <li><strong>Essential:</strong> Required for the site to function</li>
              <li><strong>Analytics:</strong> Help us understand performance and usage</li>
              <li><strong>Marketing:</strong> With consent, personalize offers/ads</li>
            </ul>
            <p>You can manage cookies in your browser settings. Disabling some may affect site functionality.</p>
          </article>

          <article id="third-parties" className="privacy-block">
            <h2><span>8.</span> Third-Party Links</h2>
            <p>
              Our website may link to other sites. Their privacy practices are not our responsibility. 
              Please review their policies before providing personal data.
            </p>
          </article>

          <article id="transfers" className="privacy-block">
            <h2><span>9.</span> International Data Transfers</h2>
            <p>
              Your data may be processed in countries outside your own (e.g., where our servers or partners are located).
              We take steps to ensure appropriate safeguards for such transfers.
            </p>
          </article>

          <article id="children" className="privacy-block">
            <h2><span>10.</span> Children’s Privacy</h2>
            <p>
              Our services are not directed to children under 18 (or the minimum age in your jurisdiction).
              We do not knowingly collect data from children without parental consent.
            </p>
          </article>

          <article id="changes" className="privacy-block">
            <h2><span>11.</span> Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. We’ll post updates here and revise the “Effective Date.”
              Material changes may also be communicated via email or notice on the site.
            </p>
          </article>

          <article id="contact" className="privacy-block">
            <h2><span>12.</span> Contact Us</h2>
            <div className="card contact-card">
              <p><i className="fas fa-building"></i> <strong>[JOUJO Safaris]</strong></p>
              <p><i className="fas fa-envelope"></i> <a href="mailto:support@joujosafaris.com">support@joujosafaris.com</a></p>
              <p><i className="fas fa-phone"></i> <a href="tel:+254729864933">+254729864933</a></p>
              <p><i className="fas fa-map-marker-alt"></i> [Nairobi, Kenya]</p>
            </div>
          </article>

          <p className="last-updated"><i className="far fa-clock"></i> Last updated: [22nd August 2025]</p>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Privacy;
